import noContent from '@/assets/images/no-content.webp';
import { AutoScroller, IconButton, IconFont } from '@/components';
import { useIsMounted } from '@/hooks';
import type { ITableTag, ITag } from '@/services/custom/extractConfig';
import type { FormInstance, InputNumberProps } from 'antd';
import {
  Button,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Space,
  Tooltip,
  Typography,
} from 'antd';
import type { FC } from 'react';
import { useContext } from 'react';
import { readonlyContext } from '../../../context';
import {
  ALL,
  CELL,
  CELL_FILTER_OPTIONS,
  COLUMN,
  CUSTOM,
  DOWN,
  INITIAL_RULE,
  INITIAL_VALUES,
  KEYWORD,
  LEFT,
  NEGATIVE,
  ORDER,
  POSITIVE,
  RANGE_OPTIONS,
  RIGHT,
  ROW,
  RULE_MODE_OPTIONS,
  TABLE_MODE_OPTIONS,
  TARGET,
  UP,
} from './const';
import type {
  All,
  Cell,
  Column,
  Custom,
  First,
  IFormValue,
  Keyword,
  Last,
  Order,
  Row,
  RowColumn,
  Target,
} from './type';

const { Item: FormItem, List: FormList } = Form;
const { Group: RadioGroup } = Radio;
const { Compact } = Space;

export const TableForm: FC<{
  form: FormInstance<IFormValue>;
  fields: {
    text_tags: ITag[];
    table_tags: ITableTag[];
  };
  initialValues?: Partial<IFormValue>;
}> = (props) => {
  // props
  const { form, fields, initialValues = INITIAL_VALUES } = props;
  const { text_tags = [], table_tags = [] } = fields;
  const TEXT_OPTIONS = text_tags.map((tag) => ({
    label: tag.tag_name,
    value: tag.tag_code,
  }));
  const TABLE_OPTIONS = table_tags.map((tag) => ({
    label: (
      <span className="flex gap-1">
        <IconFont type="icon-biaogeshibie" />
        {tag.table_name}
      </span>
    ),
    title: tag.table_code,
    options: tag.tags.map((tag) => ({
      label: tag.tag_name,
      value: tag.tag_code,
    })),
  }));
  // context
  const readonly = useContext(readonlyContext);
  const editable = !readonly;
  // is mounted
  const isMounted = useIsMounted();

  return (
    <Form
      layout="vertical"
      form={form}
      initialValues={initialValues}
      disabled={readonly}
    >
      <FormItem label="抽取范围">
        <section className="flex gap-4 w-full">
          <FormItem<IFormValue> className="flex-1 mb-0" name="extraction_range">
            <Select placeholder="请选择抽取范围" options={RANGE_OPTIONS} />
          </FormItem>
          <FormItem<IFormValue> noStyle shouldUpdate={() => true}>
            {({ getFieldValue }) => {
              const range = getFieldValue('extraction_range') as
                | All
                | First
                | Last
                | Target;

              console.log('🚀 -> range:', range);
              if (range === TARGET) {
                return (
                  <FormItem<IFormValue>
                    className="flex-2 mb-0"
                    name="pages"
                    rules={[
                      {
                        pattern: /^(\d+(-\d+)?)(,\d+(-\d+)?)*$/,
                        message: '请输入正确的格式',
                        warningOnly: true,
                      },
                    ]}
                  >
                    <Input allowClear placeholder="请输入页码" maxLength={32} />
                  </FormItem>
                );
              }
            }}
          </FormItem>
        </section>
      </FormItem>
      <FormItem label="表格对象">
        <section className="flex gap-4 w-full">
          <FormItem<IFormValue> name="table_order_type" className="flex-1 mb-0">
            <Select options={TABLE_MODE_OPTIONS} />
          </FormItem>
          <FormItem<IFormValue> noStyle dependencies={['table_order_type']}>
            {({ getFieldValue }) => {
              const type = getFieldValue('table_order_type') as Keyword | Order;

              if (type === ORDER) {
                return (
                  <Compact className="flex-2 flex w-full">
                    <FormItem<IFormValue>
                      noStyle
                      name={['table_order', 'direction']}
                    >
                      <Select
                        className="flex-1"
                        options={[
                          {
                            label: '正数',
                            value: POSITIVE,
                          },
                          {
                            label: '倒数',
                            value: NEGATIVE,
                          },
                        ]}
                      />
                    </FormItem>
                    <FormItem<IFormValue>
                      noStyle
                      name={['table_order', 'offset']}
                      rules={[
                        {
                          required: true,
                          message: '',
                          warningOnly: true,
                          type: 'number',
                        },
                      ]}
                    >
                      <InputNumber
                        className="flex-3"
                        min={1}
                        max={99999}
                        placeholder="请输入偏移量"
                      />
                    </FormItem>
                  </Compact>
                );
              }

              return (
                <FormItem<IFormValue>
                  className="flex-2 mb-0"
                  name="keywords"
                  rules={[{ required: true, message: '', warningOnly: true }]}
                >
                  <Input allowClear placeholder="请输入关键词或正则表达式" />
                </FormItem>
              );
            }}
          </FormItem>
        </section>
      </FormItem>
      <FormItem<IFormValue> name="is_cross_page" valuePropName="checked">
        <Checkbox>
          <span className="flex gap-1 items-center">
            跨页拼接表格
            <Tooltip title="开启后，可支持跨页表格内容抽取">
              <IconFont
                className="text-base hover:text-primary"
                type="icon-bangzhu2"
              />
            </Tooltip>
          </span>
        </Checkbox>
      </FormItem>
      <FormItem className="mb-0" label="字段和规则">
        <FormList name="rules">
          {(fields, { add, remove }) => {
            if (fields.length === 0) {
              return (
                <section className="flex flex-col justify-center items-center first-line:h-96">
                  <img src={noContent} width={200} alt="no content" />
                  <Button
                    type="dashed"
                    htmlType="button"
                    icon={<IconFont type="icon-tianjia1" />}
                    onClick={() => add(INITIAL_RULE)}
                  >
                    新增抽取点
                  </Button>
                </section>
              );
            }

            return (
              <section className="flex flex-col gap-5">
                {fields.map((field) => {
                  const { key, name } = field;

                  return (
                    <AutoScroller
                      className="relative px-4 pt-3 rounded bg-main-bg transition hover:shadow-md"
                      enabled={isMounted}
                      key={key}
                    >
                      <FormItem label="字段" name={[name, 'tag_code']}>
                        <Select
                          placeholder="请选择字段"
                          options={[
                            {
                              label: (
                                <span className="flex gap-1">
                                  <IconFont type="icon-wenzishibie" />
                                  文本字段
                                </span>
                              ),
                              title: 'TEXT',
                              options: TEXT_OPTIONS,
                            },
                            ...TABLE_OPTIONS,
                          ]}
                        />
                      </FormItem>
                      <FormItem label="规则" name={[name, 'position_type']}>
                        <RadioGroup
                          block
                          options={RULE_MODE_OPTIONS}
                          optionType="button"
                          buttonStyle="solid"
                        />
                      </FormItem>
                      <FormItem<IFormValue>
                        noStyle
                        dependencies={[['rules', name, 'position_type']]}
                      >
                        {({ getFieldValue }) => {
                          const mode = getFieldValue([
                            'rules',
                            name,
                            'position_type',
                          ]) as Row | Column | Cell;
                          const createOffset = (
                            renderMode: Row | Column,
                            filterMode: Keyword | Order,
                            inputNumberProps: InputNumberProps,
                            ...partialNames: string[]
                          ) => {
                            const _k = filterMode === KEYWORD;
                            const _r = renderMode === ROW;

                            return (
                              <section className="flex gap-4">
                                <FormItem
                                  className="flex-1"
                                  name={[name, ...partialNames, 'direction']}
                                >
                                  <Select
                                    options={[
                                      {
                                        label: _k
                                          ? `向${_r ? '上' : '左'}偏移`
                                          : '正数',
                                        value: _k ? (_r ? UP : LEFT) : POSITIVE,
                                      },
                                      {
                                        label: _k
                                          ? `向${_r ? '下' : '右'}偏移`
                                          : '倒数',
                                        value: _k
                                          ? _r
                                            ? DOWN
                                            : RIGHT
                                          : NEGATIVE,
                                      },
                                    ]}
                                  />
                                </FormItem>
                                <FormItem
                                  className="flex-2"
                                  name={[name, ...partialNames, 'offset']}
                                >
                                  <InputNumber
                                    className="w-full"
                                    {...inputNumberProps}
                                  />
                                </FormItem>
                              </section>
                            );
                          };
                          const createBlock = (
                            renderMode: Row | Column,
                            ...partialNames: string[]
                          ) => {
                            const _r = renderMode === ROW;

                            return (
                              <>
                                <FormItem
                                  name={[
                                    name,
                                    ...partialNames,
                                    'position_method',
                                  ]}
                                >
                                  <Select
                                    options={[
                                      {
                                        label: `关键词所在${_r ? '行' : '列'}`,
                                        value: KEYWORD,
                                      },
                                      {
                                        label: `${_r ? '行' : '列'}顺序`,
                                        value: ORDER,
                                      },
                                    ]}
                                  />
                                </FormItem>
                                <FormItem
                                  noStyle
                                  dependencies={[
                                    [
                                      'rules',
                                      name,
                                      ...partialNames,
                                      'position_method',
                                    ],
                                  ]}
                                >
                                  {({ getFieldValue }) => {
                                    const mode = getFieldValue([
                                      'rules',
                                      name,
                                      ...partialNames,
                                      'position_method',
                                    ]) as Keyword | Order;

                                    const OFFSET = createOffset(
                                      renderMode,
                                      mode,
                                      {
                                        suffix: _r ? '行' : '列',
                                        min: mode === KEYWORD ? 0 : 1,
                                      },
                                      ...partialNames,
                                      'offset_config',
                                    );

                                    if (mode === ORDER) {
                                      return OFFSET;
                                    }

                                    return (
                                      <FormItem className="mb-0">
                                        <FormItem
                                          name={[
                                            name,
                                            ...partialNames,
                                            'keyword',
                                          ]}
                                          rules={[
                                            {
                                              required: true,
                                              message: '',
                                              warningOnly: true,
                                            },
                                          ]}
                                        >
                                          <Input
                                            allowClear
                                            placeholder="请输入关键词或正则表达式"
                                          />
                                        </FormItem>
                                        {OFFSET}
                                      </FormItem>
                                    );
                                  }}
                                </FormItem>
                              </>
                            );
                          };

                          switch (mode) {
                            case ROW: {
                              const key = [
                                'rules',
                                name,
                                'row_content_config',
                                'colum_range_config',
                                'range_type',
                              ] as [
                                'rules',
                                number,
                                'row_content_config',
                                'colum_range_config',
                                'range_type',
                              ];

                              return (
                                <FormItem name={[name, 'row_content_config']}>
                                  <FormItem
                                    className="mb-0"
                                    label="行定位"
                                    name={[
                                      name,
                                      'row_content_config',
                                      'row_config',
                                    ]}
                                  >
                                    {createBlock(
                                      ROW,
                                      'row_content_config',
                                      'row_config',
                                    )}
                                  </FormItem>
                                  <FormItem
                                    className="mb-0"
                                    label="列范围"
                                    name={[
                                      name,
                                      'row_content_config',
                                      'colum_range_config',
                                    ]}
                                  >
                                    <FormItem
                                      name={[
                                        name,
                                        'row_content_config',
                                        'colum_range_config',
                                        'range_type',
                                      ]}
                                    >
                                      <Select
                                        options={[
                                          {
                                            label: '全部列',
                                            value: ALL,
                                          },
                                          {
                                            label: '自定义',
                                            value: CUSTOM,
                                          },
                                        ]}
                                      />
                                    </FormItem>
                                    <FormItem<IFormValue>
                                      noStyle
                                      dependencies={[key]}
                                    >
                                      {({ getFieldValue }) => {
                                        const mode = getFieldValue(key) as
                                          | All
                                          | Custom;

                                        if (mode === CUSTOM) {
                                          return (
                                            <>
                                              <FormItem
                                                className="mb-0"
                                                label="列起始"
                                                name={[
                                                  name,
                                                  'row_content_config',
                                                  'colum_range_config',
                                                  'begin',
                                                ]}
                                              >
                                                {createBlock(
                                                  COLUMN,
                                                  'row_content_config',
                                                  'colum_range_config',
                                                  'begin',
                                                )}
                                              </FormItem>
                                              <FormItem
                                                className="mb-0"
                                                label="列结束"
                                                name={[
                                                  name,
                                                  'row_content_config',
                                                  'colum_range_config',
                                                  'end',
                                                ]}
                                              >
                                                {createBlock(
                                                  COLUMN,
                                                  'row_content_config',
                                                  'colum_range_config',
                                                  'end',
                                                )}
                                              </FormItem>
                                            </>
                                          );
                                        }

                                        return null;
                                      }}
                                    </FormItem>
                                  </FormItem>
                                </FormItem>
                              );
                            }
                            case COLUMN: {
                              const key = [
                                'rules',
                                name,
                                'col_content_config',
                                'row_range_config',
                                'range_type',
                              ] as [
                                'rules',
                                number,
                                'col_content_config',
                                'row_range_config',
                                'range_type',
                              ];

                              return (
                                <FormItem name={[name, 'col_content_config']}>
                                  <FormItem
                                    className="mb-0"
                                    label="列定位"
                                    name={[
                                      name,
                                      'col_content_config',
                                      'colum_config',
                                    ]}
                                  >
                                    {createBlock(
                                      COLUMN,
                                      'col_content_config',
                                      'colum_config',
                                    )}
                                  </FormItem>
                                  <FormItem
                                    className="mb-0"
                                    label="行范围"
                                    name={[
                                      name,
                                      'col_content_config',
                                      'row_range_config',
                                    ]}
                                  >
                                    <FormItem
                                      name={[
                                        name,
                                        'col_content_config',
                                        'row_range_config',
                                        'range_type',
                                      ]}
                                    >
                                      <Select
                                        options={[
                                          {
                                            label: '全部行',
                                            value: ALL,
                                          },
                                          {
                                            label: '自定义',
                                            value: CUSTOM,
                                          },
                                        ]}
                                      />
                                    </FormItem>
                                    <FormItem<IFormValue>
                                      noStyle
                                      dependencies={[key]}
                                    >
                                      {({ getFieldValue }) => {
                                        const mode = getFieldValue(key) as
                                          | All
                                          | Custom;

                                        if (mode === 'CUSTOM') {
                                          return (
                                            <>
                                              <FormItem
                                                className="mb-0"
                                                label="行起始"
                                                name={[
                                                  name,
                                                  'col_content_config',
                                                  'row_range_config',
                                                  'begin',
                                                ]}
                                              >
                                                {createBlock(
                                                  ROW,
                                                  'col_content_config',
                                                  'row_range_config',
                                                  'begin',
                                                )}
                                              </FormItem>
                                              <FormItem
                                                className="mb-0"
                                                label="行结束"
                                                name={[
                                                  name,
                                                  'col_content_config',
                                                  'row_range_config',
                                                  'end',
                                                ]}
                                              >
                                                {createBlock(
                                                  ROW,
                                                  'col_content_config',
                                                  'row_range_config',
                                                  'end',
                                                )}
                                              </FormItem>
                                            </>
                                          );
                                        }

                                        return null;
                                      }}
                                    </FormItem>
                                  </FormItem>
                                </FormItem>
                              );
                            }
                            case CELL: {
                              const key = [
                                'rules',
                                name,
                                'cell_content_config',
                                'grid_position',
                              ] as [
                                'rules',
                                number,
                                'cell_content_config',
                                'grid_position',
                              ];

                              return (
                                <FormItem name={[name, 'cell_content_config']}>
                                  <FormItem
                                    label="单元格定位"
                                    name={[
                                      name,
                                      'cell_content_config',
                                      'grid_position',
                                    ]}
                                  >
                                    <Select options={CELL_FILTER_OPTIONS} />
                                  </FormItem>
                                  <FormItem<IFormValue>
                                    noStyle
                                    dependencies={[key]}
                                  >
                                    {({ getFieldValue }) => {
                                      const mode = getFieldValue(key) as
                                        | Keyword
                                        | RowColumn;
                                      const OFFSET = (
                                        <FormItem label="单元格偏移">
                                          {createOffset(
                                            COLUMN,
                                            KEYWORD,
                                            { suffix: '个单元格', min: 0 },
                                            'cell_content_config',
                                            'left_right_offset',
                                          )}
                                          {createOffset(
                                            ROW,
                                            KEYWORD,
                                            { suffix: '个单元格', min: 0 },
                                            'cell_content_config',
                                            'up_down_offset',
                                          )}
                                        </FormItem>
                                      );

                                      if (mode === KEYWORD) {
                                        return (
                                          <>
                                            <FormItem
                                              name={[
                                                name,
                                                'cell_content_config',
                                                'keyword',
                                              ]}
                                              rules={[
                                                {
                                                  required: true,
                                                  message: '',
                                                  warningOnly: true,
                                                },
                                              ]}
                                            >
                                              <Input
                                                allowClear
                                                placeholder="请输入关键词或正则表达式"
                                              />
                                            </FormItem>
                                            {OFFSET}
                                          </>
                                        );
                                      }

                                      return (
                                        <>
                                          <FormItem
                                            className="mb-0"
                                            label="行定位"
                                          >
                                            {createBlock(
                                              ROW,
                                              'cell_content_config',
                                              'row_config',
                                            )}
                                          </FormItem>
                                          <FormItem
                                            className="mb-0"
                                            label="列定位"
                                          >
                                            {createBlock(
                                              COLUMN,
                                              'cell_content_config',
                                              'column_config',
                                            )}
                                          </FormItem>
                                          {OFFSET}
                                        </>
                                      );
                                    }}
                                  </FormItem>
                                </FormItem>
                              );
                            }
                          }
                        }}
                      </FormItem>
                      {editable && (
                        <IconButton
                          active
                          circle
                          title="删除"
                          className="absolute -top-3 right-4 shadow hover:scale-110 hover:shadow-md hover:text-error"
                          iconType="icon-shanchu1"
                          onClick={() => remove(name)}
                        />
                      )}
                    </AutoScroller>
                  );
                })}
                <Button
                  block
                  className="sticky bottom-0"
                  type="dashed"
                  htmlType="button"
                  icon={<IconFont type="icon-tianjia1" />}
                  onClick={() => add(INITIAL_RULE)}
                >
                  新增抽取点
                </Button>
              </section>
            );
          }}
        </FormList>
      </FormItem>
      <Form.Item noStyle shouldUpdate>
        {(form) => (
          <Typography>
            <pre>{JSON.stringify(form.getFieldsValue(), null, 2)}</pre>
          </Typography>
        )}
      </Form.Item>
    </Form>
  );
};
